// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import PasswordRequirements from "@store/Library/Components/Toolkit/PasswordRequirements";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import createAccountRequest from "@store/Framework/Handlers/Account/CreateAccountRequest";
import AccountRequestFormReCaptcha from "@store/Library/Components/AccountRequestFormReCaptcha";
import { formFieldTitleStyles } from "@store/Themes/common/mixins/formField";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    emailRecipient = "emailRecipient",
    enableRecaptcha = "enableRecaptcha",
    lastNameFieldLabel = "lastNameFieldLabel",
    firstNameFieldLabel = "firstNameFieldLabel",
    customerTypeLabel = "customerTypeLabel",
    emailFieldLabel = "emailFieldLabel",
    phoneFieldLabel = "phoneFieldLabel",
    companyFieldLabel = "companyFieldLabel",
    businessTypeFieldLabel = "businessTypeFieldLabel",
    customerTypeHintText = "customerTypeHintText",
    customerTypes = "customerTypes",
    lastNameFieldPlaceholder = "lastNameFieldPlaceholder",
    firstNameFieldPlaceholder = "firstNameFieldPlaceholder",
    emailFieldPlaceholder = "emailFieldPlaceholder",
    phoneFieldPlaceholder = "phoneFieldPlaceholder",
    companyFieldPlaceholder = "companyFieldPlaceholder",
    businessTypeFieldPlaceholder = "businessTypeFieldPlaceholder",
    lastNameFieldError = "lastNameFieldError",
    firstNameFieldError = "firstNameFieldError",
    customerTypeFieldError = "customerTypeFieldError",
    emailFieldError = "emailFieldError",
    phoneFieldError = "phoneFieldError",
    invalidPhoneNumberFieldError = "invalidPhoneNumberFieldError",
    companyFieldError = "companyFieldError",
    businessTypeFieldError = "businessTypeFieldError",
    accessRequestNotificationMessage = "accessRequestNotificationMessage",
    accessRequestFailureNotificationMessage = "accessRequestFailureNotificationMessage",
    accessRequestButtonDisabledText = "accessRequestButtonDisabledText",
    userNameFieldError = "userNameFieldError",
    emailRequiredFieldErrorMessage = "emailRequiredFieldErrorMessage",
    emailInvalidFieldMessage = "emailInvalidFieldMessage",
}

interface CustomerTypeModel {
    fields: {
        customerType: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipient]: string;
        [fields.enableRecaptcha]: boolean;
        [fields.lastNameFieldLabel]: string;
        [fields.firstNameFieldLabel]: string;
        [fields.customerTypeLabel]: string;
        [fields.emailFieldLabel]: string;
        [fields.phoneFieldLabel]: string;
        [fields.companyFieldLabel]: string;
        [fields.businessTypeFieldLabel]: string;
        [fields.customerTypeHintText]: string;
        [fields.customerTypes]: CustomerTypeModel[];
        [fields.lastNameFieldPlaceholder]: string;
        [fields.firstNameFieldPlaceholder]: string;
        [fields.emailFieldPlaceholder]: string;
        [fields.phoneFieldPlaceholder]: string;
        [fields.companyFieldPlaceholder]: string;
        [fields.businessTypeFieldPlaceholder]: string;
        [fields.lastNameFieldError]: string;
        [fields.firstNameFieldError]: string;
        [fields.customerTypeFieldError]: string;
        [fields.emailFieldError]: string;
        [fields.phoneFieldError]: string;
        [fields.invalidPhoneNumberFieldError]: string;
        [fields.companyFieldError]: string;
        [fields.businessTypeFieldError]: string;
        [fields.accessRequestNotificationMessage]: string;
        [fields.accessRequestFailureNotificationMessage]: string;
        [fields.accessRequestButtonDisabledText]: string;
        [fields.userNameFieldError]: string;
        [fields.emailRequiredFieldErrorMessage]: string;
        [fields.emailInvalidFieldMessage]: string;
    };
}

const mapDispatchToProps = {
    addAccount,
    createAccountRequest,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{}, boolean>(validateReCaptcha),
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReturnFormStyles {
    wrapper: InjectableCss;
    internalWrapper: InjectableCss;
    form?: InjectableCss;
    textField?: TextFieldProps;
    customerTypeSelect?: SelectPresentationProps;
    accessFormGridContainer?: GridContainerProps;
    rowGridContainer?: GridContainerProps;
    requestAccessButton?: ButtonPresentationProps;
    submitAccessButtonGridItem?: GridItemProps;
    rowGridItem?: GridItemProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    error?: TypographyProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

const fullWidthItem: GridItemProps = {
    width: [12, 12, 12, 12, 12],
    css: formFieldTitleStyles(),
};

const halfWidthItem: GridItemProps = {
    width: [12, 12, 6, 6, 6],
    css: formFieldTitleStyles(),
};

export const returnFormStyles: ReturnFormStyles = {
    wrapper: {
        css: css`
            margin: 1rem 0;
            background-color: ${getColor("common.accent")};
            border-radius: ${pxToRem(10)};
        `,
    },
    internalWrapper: {
        css: css`
            padding: ${pxToRem(45, 35)};
        `,
    },
    rowGridItem: fullWidthItem,
    leftGridItem: halfWidthItem,
    rightGridItem: halfWidthItem,
    submitAccessButtonGridItem: {
        width: [12, 12, 12, 12, 12],
        align: "bottom",
    },
    accessFormGridContainer: {
        offsetCss: css`
            display: flex;
            flex-direction: column;
        `,
    },
    rowGridContainer: {
        offsetCss: css`
            display: flex;
        `,
    },
    requestAccessButton: {
        variant: "primary",
        sizeVariant: "large",
    },
    error: { color: "danger" },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const CenteringWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const styles = returnFormStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const PHONE_PATTERN = /(\d{3})(\d{3})(\d{4})/;
const StyledForm = getStyledWrapper("form");

const AccountRequestForm = ({
    fields: {
        emailRecipient,
        enableRecaptcha,
        lastNameFieldLabel,
        firstNameFieldLabel,
        customerTypeLabel,
        phoneFieldLabel,
        companyFieldLabel,
        businessTypeFieldLabel,
        customerTypeHintText,
        customerTypes,
        lastNameFieldPlaceholder,
        firstNameFieldPlaceholder,
        phoneFieldPlaceholder,
        companyFieldPlaceholder,
        businessTypeFieldPlaceholder,
        lastNameFieldError,
        firstNameFieldError,
        customerTypeFieldError,
        phoneFieldError,
        invalidPhoneNumberFieldError,
        // companyFieldError,
        // businessTypeFieldError,
        accessRequestButtonDisabledText,
        userNameFieldError,
        emailRequiredFieldErrorMessage,
        emailInvalidFieldMessage,
    },
    id,
    accountSettings,
    returnUrl,
    validatePassword,
    validateReCaptcha,
    addAccount,
    createAccountRequest,
    signIn,
}: Props) => {
    const userNameRequiredFieldMessage = userNameFieldError;
    const emailRequiredFieldMessage = emailRequiredFieldErrorMessage;
    const emailFieldMessage = emailInvalidFieldMessage;
    const { useEmailAsUserName } = accountSettings;

    const [email, setEmail] = useState("");
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [customerType, setcustomerType] = useState("");
    const [phone, setPhone] = useState("");
    const [company, setCompany] = useState("");
    const [typeOfBusiness, setTypeOfBusiness] = useState("");
    const [emailError, setEmailError] = useState<ReactNode>(emailRequiredFieldMessage);
    const [userNameError, setUserNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : userNameRequiredFieldMessage,
    );
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitClicked, setIsSubmitClicked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [rawPhone, setRawPhone] = useState("");

    const lastNameError = isSubmitClicked && lastName === "" ? lastNameFieldError : "";
    const firstNameError = isSubmitClicked && firstName === "" ? firstNameFieldError : "";
    const customerTypeError = isSubmitClicked && customerType === "" ? customerTypeFieldError : "";

    const toasterContext = React.useContext(ToasterContext);

    const validatePhoneNumber = (phoneNumber: string) => {
        const cleaned = phoneNumber.replace(/\D/g, "");

        if (!cleaned) {
            return phoneFieldError;
        }

        if (cleaned.length !== 10) {
            return invalidPhoneNumberFieldError;
        }

        return "";
    };

    const phoneError = isSubmitClicked ? validatePhoneNumber(phone) : "";

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateUserName = (name: string) => {
        const errorMessage = !name && !useEmailAsUserName ? userNameRequiredFieldMessage : "";
        setUserNameError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const formValidationHandler = () => {
        if (
            emailError ||
            phoneError ||
            passwordErrorMessage ||
            confirmPasswordErrorMessage ||
            userNameError ||
            !lastName ||
            !firstName ||
            !customerType ||
            !email
        ) {
            return false;
        }
        return true;
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const userChangeHandler = (name: string) => {
        validateUserName(name);
        setUserName(name);
    };

    const passwordChangeHandler = (newPassword: string) => {
        validateConfirmPassword(newPassword, confirmPassword);
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(newPassword);
    };

    const confirmPasswordChangeHandler = (newConfirmPassword: string) => {
        setConfirmPassword(newConfirmPassword);
        validateConfirmPassword(password, newConfirmPassword);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        setTimeout(() => {
            signIn({
                userName: useEmailAsUserName ? email : userName,
                password,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsSubmitClicked(true);
        event.preventDefault();

        if (enableRecaptcha) {
            const isReCaptchaValid = await validateReCaptcha({});
            if (!isReCaptchaValid) {
                return toasterContext.addToast({
                    body: "Recaptcha failed",
                    messageType: "danger",
                });
            }
        }

        setIsLoading(true);

        const formValidated = formValidationHandler();

        if (formValidated) {
            try {
                await new Promise<void>((resolve, reject) => {
                    addAccount({
                        email,
                        userName,
                        password,
                        isSubscribed: false,
                        onSuccess: () => resolve(),
                        onError: reject,
                    });
                });

                await new Promise<void>((resolve, reject) => {
                    createAccountRequest({
                        userName,
                        email,
                        password,
                        isSubscribed: false,
                        firstName,
                        lastName,
                        customerType,
                        phone,
                        company,
                        typeOfBusiness,
                        emailRecipient,
                        onSuccess: () => resolve(),
                        onError: reject,
                    });
                });

                onSuccessHandler();
            } catch (error) {
                setErrorMessage(error);
                toasterContext.addToast({ body: error, messageType: "danger" });
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const showHintModal = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.preventDefault();
        setModalIsOpen(true);
    };

    const pwHintLink = (
        <Link onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => showHintModal(e)}>
            {translate("showHint")}
        </Link>
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.internalWrapper}>
                {useZoneIsActive(id, "ContentTop") && <Zone contentId={id} zoneName="ContentTop" />}
                <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.accessFormGridContainer}>
                        {isLoading && (
                            <>
                                <CenteringWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.loadingSpinner} />
                                </CenteringWrapper>
                            </>
                        )}
                        {!isLoading && (
                            <>
                                <GridItem {...styles.rowGridItem}>
                                    <GridContainer {...styles.rowGridContainer}>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                {...styles.textField}
                                                type="email"
                                                label={translate("Email Address")}
                                                onChange={e => emailChangeHandler(e.currentTarget.value)}
                                                value={email}
                                                data-test-selector="createAccount_email"
                                                maxLength={256}
                                                required
                                                error={isSubmitClicked && emailError}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.rightGridItem}>
                                            <TextField
                                                {...styles.textField}
                                                label={translate("User Name")}
                                                value={userName}
                                                data-test-selector="createAccount_userName"
                                                onChange={e => userChangeHandler(e.currentTarget.value)}
                                                error={isSubmitClicked && userNameError}
                                                maxLength={256}
                                                required
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem {...styles.rowGridItem}>
                                    <GridContainer {...styles.rowGridContainer}>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                id="password"
                                                {...styles.textField}
                                                type={showPassword ? undefined : "password"}
                                                label={translate("Password")}
                                                value={password}
                                                onChange={e => passwordChangeHandler(e.currentTarget.value)}
                                                error={isSubmitClicked && passwordErrorMessage}
                                                iconProps={{
                                                    ...styles.passwordIcon,
                                                    src: showPassword ? EyeOff : Eye,
                                                }}
                                                iconClickableProps={{
                                                    onClick: () => {
                                                        setShowPassword(!showPassword);
                                                    },
                                                    type: "button",
                                                }}
                                                clickableText={password}
                                                data-test-selector="createAccount_password"
                                                required
                                                hint={pwHintLink}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.rightGridItem}>
                                            <TextField
                                                id="confirm-password"
                                                {...styles.textField}
                                                type={showConfirmPassword ? undefined : "password"}
                                                label={translate("Confirm Password")}
                                                value={confirmPassword}
                                                onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                                                error={isSubmitClicked && confirmPasswordErrorMessage}
                                                iconProps={{
                                                    ...styles.confirmPasswordIcon,
                                                    src: showConfirmPassword ? EyeOff : Eye,
                                                }}
                                                iconClickableProps={{
                                                    onClick: () => {
                                                        setShowConfirmPassword(!showConfirmPassword);
                                                    },
                                                    type: "button",
                                                }}
                                                clickableText={confirmPassword}
                                                data-test-selector="createAccount_confirmPassword"
                                                required
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem {...styles.rowGridItem}>
                                    <GridContainer {...styles.rowGridContainer}>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                id="firstName"
                                                {...styles.textField}
                                                label={firstNameFieldLabel}
                                                onChange={e => setFirstName(e.currentTarget.value)}
                                                value={firstName}
                                                data-test-selector="first-name"
                                                type="text"
                                                error={firstNameError}
                                                required
                                                placeholder={firstNameFieldPlaceholder}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                id="lastName"
                                                {...styles.textField}
                                                label={lastNameFieldLabel}
                                                onChange={e => setLastName(e.currentTarget.value)}
                                                value={lastName}
                                                data-test-selector="last-name"
                                                type="text"
                                                error={lastNameError}
                                                required
                                                placeholder={lastNameFieldPlaceholder}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem {...styles.rowGridItem}>
                                    <GridContainer {...styles.rowGridContainer}>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                id="phone"
                                                {...styles.textField}
                                                label={phoneFieldLabel}
                                                onChange={e => {
                                                    const digitsOnly = e.target.value.replace(/\D/g, "");
                                                    const match = digitsOnly.match(PHONE_PATTERN);
                                                    const formattedPhone = match
                                                        ? `${match[1]}-${match[2]}-${match[3]}`
                                                        : e.target.value;
                                                    setPhone(formattedPhone);
                                                }}
                                                value={phone}
                                                data-test-selector="phone"
                                                type="text"
                                                maxLength={12}
                                                error={phoneError}
                                                required
                                                placeholder={phoneFieldPlaceholder}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.rightGridItem}>
                                            <TextField
                                                id="company"
                                                {...styles.textField}
                                                label={companyFieldLabel}
                                                onChange={e => setCompany(e.currentTarget.value)}
                                                value={company}
                                                data-test-selector="company"
                                                type="text"
                                                placeholder={companyFieldPlaceholder}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.rowGridItem}>
                                    <GridContainer {...styles.rowGridContainer}>
                                        <GridItem {...styles.leftGridItem}>
                                            <TextField
                                                id="business"
                                                {...styles.textField}
                                                label={businessTypeFieldLabel}
                                                onChange={e => setTypeOfBusiness(e.currentTarget.value)}
                                                value={typeOfBusiness}
                                                data-test-selector="business_type"
                                                type="text"
                                                placeholder={businessTypeFieldPlaceholder}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.rightGridItem}>
                                            <Select
                                                {...styles.customerTypeSelect}
                                                label={customerTypeLabel}
                                                required
                                                value={customerType}
                                                error={customerTypeError}
                                                onChange={e => setcustomerType(e.currentTarget.value)}
                                            >
                                                <option value="">{customerTypeHintText}</option>
                                                {customerTypes.map(customerType => (
                                                    <option
                                                        key={customerType.fields.customerType}
                                                        value={customerType.fields.customerType}
                                                    >
                                                        {customerType.fields.customerType}
                                                    </option>
                                                ))}
                                            </Select>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                {enableRecaptcha && (
                                    <GridItem {...styles.submitAccessButtonGridItem}>
                                        <AccountRequestFormReCaptcha enable={enableRecaptcha} />
                                    </GridItem>
                                )}
                                <GridItem {...styles.submitAccessButtonGridItem}>
                                    <FormActions>
                                        <Button
                                            {...styles.requestAccessButton}
                                            type="submit"
                                            data-test-selector="request_submit"
                                            disabled={isLoading}
                                        >
                                            {translate("Submit account Request")}
                                        </Button>
                                    </FormActions>
                                </GridItem>
                                <GridItem {...styles.submitAccessButtonGridItem}>
                                    {isLoading && (
                                        <>
                                            <Typography {...styles.error}>{accessRequestButtonDisabledText}</Typography>
                                        </>
                                    )}
                                </GridItem>
                            </>
                        )}
                    </GridContainer>
                </StyledForm>
                {useZoneIsActive(id, "ContentBottom") && <Zone contentId={id} zoneName="ContentBottom" />}
            </StyledWrapper>
            <Modal headline={translate("Password Requirements")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                <StyledWrapper>
                    <PasswordRequirements data-test-selector="createAccount_passwordRequirements" />
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountRequestForm),
    definition: {
        group: "Common",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.emailRecipient,
                displayName: "Email recipient",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Enable Recaptcha",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.userNameFieldError,
                displayName: " User Name Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Username is required",
            },
            {
                name: fields.firstNameFieldLabel,
                displayName: "First Name Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "First Name",
            },
            {
                name: fields.firstNameFieldPlaceholder,
                displayName: "First Name Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your first name here",
            },
            {
                name: fields.firstNameFieldError,
                displayName: "First Name Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter first name",
            },
            {
                name: fields.lastNameFieldLabel,
                displayName: "Last Name Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Last Name",
            },
            {
                name: fields.lastNameFieldPlaceholder,
                displayName: "Last Name Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your last name here",
            },
            {
                name: fields.lastNameFieldError,
                displayName: "Last Name Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter last name",
            },
            {
                name: fields.customerTypeLabel,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Customer Type",
                isRequired: true,
            },
            {
                name: fields.customerTypeHintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Select Customer Type",
                isRequired: true,
            },
            {
                name: fields.customerTypes,
                displayName: "Customer Types",
                editorTemplate: "ListField",
                defaultValue: [{ fields: { customerType: "New User" } }, { fields: { customerType: "Existing User" } }],
                getDisplay: (item: HasFields) => {
                    return item.fields.customerType;
                },
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "customerType",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.customerTypeFieldError,
                displayName: "Customer Type Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please select a customer type",
            },
            {
                name: fields.emailFieldLabel,
                displayName: "Email Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Email",
            },
            {
                name: fields.emailFieldPlaceholder,
                displayName: "Email Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your email here",
            },
            {
                name: fields.emailFieldError,
                displayName: "Email Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter email",
            },
            {
                name: fields.emailRequiredFieldErrorMessage,
                displayName: "Email Required Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Email Address is required",
            },
            {
                name: fields.emailInvalidFieldMessage,
                displayName: "Email Invalid Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Email Address is invalid",
            },
            {
                name: fields.phoneFieldLabel,
                displayName: "Phone Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Phone",
            },
            {
                name: fields.phoneFieldPlaceholder,
                displayName: "Phone Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your phone number here (XXX) XXX-XXXX",
            },
            {
                name: fields.phoneFieldError,
                displayName: "Phone Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter phone number",
            },
            {
                name: fields.invalidPhoneNumberFieldError,
                displayName: "Invalid Phone Number Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter a valid phone number",
            },
            {
                name: fields.companyFieldLabel,
                displayName: "Company Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Company",
            },
            {
                name: fields.companyFieldPlaceholder,
                displayName: "Company Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your company here",
            },
            {
                name: fields.companyFieldError,
                displayName: "Company Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter company",
            },
            {
                name: fields.businessTypeFieldLabel,
                displayName: "Business Type Field Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Business Type",
            },
            {
                name: fields.businessTypeFieldPlaceholder,
                displayName: "Business Type Field Placeholder",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Enter your business type here",
            },
            {
                name: fields.businessTypeFieldError,
                displayName: "Business Type Field Error",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Please enter business type",
            },
            {
                name: fields.accessRequestNotificationMessage,
                displayName: "Account Request Notification Message",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "The account request has been successfully sent",
            },
            {
                name: fields.accessRequestFailureNotificationMessage,
                displayName: "Account Request Failure Notification Message",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Account Request Failure",
            },
            {
                name: fields.accessRequestButtonDisabledText,
                displayName: "Text to show when the submit account request button is disabled",
                editorTemplate: "TextField",
                defaultValue: "Service currently unavailable",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
